import { sharedApi, getAuthToken } from "./sharedApi";
import i18n from "../config/i18n";
import { logger } from "../utils/logger";

import type {
  SpeechSynthesisRequest,
  SpeechSynthesisResponse,
  VoiceRecommendationResponse,
  SaveVoicePreferenceResponse,
  TextVoicesResponse,
  VoicesResponse,
  LanguagesListResponse,
} from "@lingoshade/api";

class SpeechService {
  /**
   * Synthesize speech using textId only - БЕЗОПАСНО
   */
  async synthesizeSpeech(
    request: SpeechSynthesisRequest,
  ): Promise<SpeechSynthesisResponse> {
    if (!request.textId) {
      throw new Error(i18n.t("practice:errors.textIdRequired"));
    }

    const payload: { textId: string; voiceId?: string } = {
      textId: request.textId,
    };
    if (request.voiceId) {
      payload.voiceId = request.voiceId;
    }

    const token = getAuthToken();
    const data = await sharedApi.speech.synthesize(payload, token);

    logger.debug("[speechService.synthesizeSpeech] response", {
      hasAudioUrl: !!data.audioUrl,
      audioUrl: data.audioUrl,
      hasAudio: !!data.audio,
      audioLength: data.audio ? data.audio.length : 0,
    });

    // Always use base64 audio from the response - no need for additional fetch
    if (data.audio) {
      data.audioUrl = this.createAudioUrl(data.audio);
      logger.debug(
        "[speechService.synthesizeSpeech] created blob URL from base64",
      );
    } else {
      throw new Error(i18n.t("practice:errors.noAudioData"));
    }

    return data;
  }

  /**
   * Get voice recommendation for a specific text (prioritizes cached audio)
   */
  async getVoiceRecommendation(
    textId: string,
  ): Promise<VoiceRecommendationResponse> {
    const token = getAuthToken();
    return sharedApi.speech.getVoiceRecommendation(textId, token);
  }

  /**
   * Get available voices for user's target language
   */
  async getAvailableVoices(): Promise<VoicesResponse> {
    // Возвращаем точный ответ API без клиентских расширений (displayName/description раньше шли из legacy)
    const token = getAuthToken();
    return sharedApi.speech.getVoices(token);
  }

  /**
   * Get all supported languages with their voices
   */
  async getSupportedLanguages(): Promise<LanguagesListResponse> {
    // Совмещаем с текущей сигнатурой shared API (без списка voices внутри каждого языка)
    const token = getAuthToken();
    return sharedApi.speech.getLanguages(token);
  }
  /**
   * Save text-voice combination for caching/favorites
   */
  async saveVoicePreference(
    textId: string,
    voiceId: string,
  ): Promise<SaveVoicePreferenceResponse> {
    // speed/audioUrl больше не поддерживаются типами SaveVoicePreferenceRequest
    const token = getAuthToken();
    return sharedApi.speech.saveVoicePreference({ textId, voiceId }, token);
  }

  /**
   * Get saved voices for a text
   */
  async getTextVoices(textId: string): Promise<TextVoicesResponse> {
    const token = getAuthToken();
    return sharedApi.speech.getTextVoices(textId, token);
  }

  /**
   * Create audio blob from base64 data
   */
  createAudioBlob(base64Audio: string): Blob {
    const audioData = atob(base64Audio);
    const audioArray = new Uint8Array(audioData.length);
    for (let i = 0; i < audioData.length; i++) {
      audioArray[i] = audioData.charCodeAt(i);
    }
    return new Blob([audioArray], { type: "audio/mpeg" });
  }

  /**
   * Create audio URL from base64 data
   */
  createAudioUrl(base64Audio: string): string {
    const blob = this.createAudioBlob(base64Audio);
    return URL.createObjectURL(blob);
  }

  /**
   * Clean up audio URL
   */
  revokeAudioUrl(url: string): void {
    URL.revokeObjectURL(url);
  }

  /**
   * Play text directly with specified voice and settings
   * Used for dictation playback with timing controls
   */
  async playText(
    text: string,
    options: {
      voiceId?: string;
      speed?: number;
      pauseBetweenWords?: number;
    } = {},
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // For now, use browser's built-in speech synthesis
        // In the future, this can be enhanced to use Azure TTS directly
        if ("speechSynthesis" in window) {
          const utterance = new SpeechSynthesisUtterance(text);

          // Configure voice if specified
          if (options.voiceId) {
            const voices = window.speechSynthesis.getVoices();
            const voice = voices.find(
              (v) =>
                v.name.includes(options.voiceId!) ||
                v.voiceURI.includes(options.voiceId!),
            );
            if (voice) {
              utterance.voice = voice;
            }
          }

          // Configure speech rate
          if (options.speed) {
            utterance.rate = options.speed;
          }

          utterance.onend = () => resolve();
          utterance.onerror = (event) =>
            reject(
              new Error(
                i18n.t("practice:errors.speechSynthesisFailed", {
                  error: event.error,
                }),
              ),
            );

          window.speechSynthesis.speak(utterance);
        } else {
          reject(new Error(i18n.t("practice:errors.speechNotSupported")));
        }
      } catch (error) {
        reject(error);
      }
    });
  }
}

// Export singleton instance
export const speechService = new SpeechService();
export default speechService;
